import { GemSalesConfig, ShopperContext, TriggerConfig } from "./types";

export function chooseTrigger(config: GemSalesConfig, context: ShopperContext): TriggerConfig | null {
  if (config.mode === "tracking") return null;

  const eligible = config.triggers.filter((trigger) => {
    if (context.dismissedSlotIds.has(trigger.id)) return false;
    if ((context.triggerImpressionCounts.get(trigger.id) ?? 0) >= (trigger.maxImpressionsPerSession ?? Infinity)) {
      return false;
    }
    if (!trigger.pageTypes.includes(context.page.type)) return false;

    if (trigger.productHandles?.length) {
      if (!context.page.productHandle || !trigger.productHandles.includes(context.page.productHandle)) {
        return false;
      }
    }

    if (trigger.sectionType && !context.seenSections.has(trigger.sectionType)) return false;
    if ((trigger.minIntentScore ?? 0) > getTriggerIntentScore(trigger, context)) return false;

    if (trigger.surface === "cart") {
      const threshold = config.commerce.freeShippingThreshold;
      const subtotal = context.page.cartSubtotal;
      if (subtotal === null || subtotal >= threshold) return false;
    }

    return hasAnchor(trigger);
  });

  return eligible.sort((a, b) => getTriggerScore(b, context) - getTriggerScore(a, context))[0] ?? null;
}

export function getTriggerScore(trigger: TriggerConfig, context: ShopperContext): number {
  const intentScore = getTriggerIntentScore(trigger, context);
  const priorityScore = trigger.priority / 100;
  const sectionScore = trigger.sectionType && context.seenSections.has(trigger.sectionType) ? 0.15 : 0;
  const cartScore = trigger.surface === "cart" ? 0.15 : 0;

  return Number((priorityScore * 0.65 + intentScore * 0.25 + sectionScore + cartScore).toFixed(4));
}

export function getTriggerIntentScore(trigger: TriggerConfig, context: ShopperContext): number {
  if (!trigger.intentTargets?.length) return 0;

  return Math.max(...trigger.intentTargets.map((intent) => context.intentProfile.scores[intent] ?? 0));
}

function hasAnchor(trigger: TriggerConfig): boolean {
  return Boolean(
    document.querySelector(`[data-gemsales-slot="${cssEscape(trigger.id)}"]`) ??
      document.querySelector(`[data-gemsales-runtime-slot="${cssEscape(trigger.runtimeSlotId)}"]`) ??
      document.querySelector(`[data-gemsales-slot="${cssEscape(trigger.runtimeSlotId)}"]`) ??
      trigger.anchorSelectors.some((selector) => document.querySelector(selector)),
  );
}

function cssEscape(value: string): string {
  return typeof CSS !== "undefined" && typeof CSS.escape === "function" ? CSS.escape(value) : value.replace(/"/g, '\\"');
}
